import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Briefcase, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";

type Leader = {
  id: string;
  name: string;
  role: string;
  slug: string;
  image_url: string | null;
  image_position: string;
  tagline: string | null;
  intro: string | null;
  content: string | null;
  city: string | null;
  experience: string | null;
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

const LeaderDetail = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: leader, isLoading, error } = useQuery({
    queryKey: ["leader", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leaders")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data as Leader | null;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (error || !leader) {
    return <Navigate to="/about" replace />;
  }

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: leader.name,
    jobTitle: leader.role,
    description: leader.intro || leader.tagline || undefined,
    image: leader.image_url || undefined,
    worksFor: {
      "@type": "Organization",
      "@id": "https://dhivishaarchitects.com/#organization",
      name: "Dhivisha Architects and Interiors",
    },
  };

  return (
    <Layout>
      <SEOHead
        title={`${leader.name} — ${leader.role}`}
        description={leader.intro || leader.tagline || `${leader.name}, ${leader.role} at Dhivisha Architects.`}
        path={`/leaders/${leader.slug}`}
        image={leader.image_url || undefined}
        type="profile"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }}
      />

      <article className="max-w-5xl mx-auto px-4 py-12 md:py-20">
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-10 group transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back to About
        </Link>

        {/* Hero */}
        <motion.header
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          className="grid md:grid-cols-[300px_1fr] gap-10 md:gap-12 items-start mb-14"
        >
          {leader.image_url && (
            <motion.div
              variants={fadeUp}
              className="relative group"
            >
              <div className="absolute -inset-2 bg-gradient-to-br from-primary/20 via-transparent to-primary/10 rounded-lg blur-sm opacity-70" />
              <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden border border-border shadow-lg ring-1 ring-primary/10">
                <img
                  src={leader.image_url}
                  alt={leader.name}
                  style={{ objectPosition: leader.image_position }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  loading="eager"
                />
              </div>
            </motion.div>
          )}

          <div>
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-[0.2em]">
                Leadership
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="heading-display text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] mb-3"
            >
              {leader.name}
            </motion.h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "left" }}
              className="h-[3px] w-20 bg-primary mb-5 rounded-full"
            />

            <motion.p
              variants={fadeUp}
              className="text-primary/90 italic text-lg mb-5 heading-display"
            >
              {leader.role}
            </motion.p>

            {leader.tagline && (
              <motion.p
                variants={fadeUp}
                className="text-foreground/80 text-base md:text-lg leading-relaxed mb-5"
              >
                {leader.tagline}
              </motion.p>
            )}

            {leader.intro && (
              <motion.p
                variants={fadeUp}
                className="text-muted-foreground leading-relaxed mb-6"
              >
                {leader.intro}
              </motion.p>
            )}

            <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
              {leader.city && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-muted border border-border text-foreground">
                  <MapPin className="w-3.5 h-3.5 text-primary" /> {leader.city}
                </span>
              )}
              {leader.experience && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-muted border border-border text-foreground">
                  <Briefcase className="w-3.5 h-3.5 text-primary" /> {leader.experience}
                </span>
              )}
            </motion.div>
          </div>
        </motion.header>

        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-3 my-12" aria-hidden>
          <span className="h-px w-16 bg-border" />
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          <span className="h-px w-16 bg-border" />
        </div>

        {/* Body */}
        {leader.content && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="prose prose-neutral max-w-none
              prose-headings:heading-display prose-headings:font-normal
              prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-5 prose-h2:pb-3
              prose-h2:text-foreground prose-h2:border-b prose-h2:border-primary/20
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-primary
              prose-p:text-foreground/85 prose-p:leading-relaxed
              prose-li:text-foreground/85 prose-li:my-1
              prose-li:marker:text-primary
              prose-strong:text-foreground prose-strong:font-semibold
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-blockquote:border-l-primary prose-blockquote:text-foreground/80 prose-blockquote:italic
              prose-ul:my-4"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{leader.content}</ReactMarkdown>
          </motion.section>
        )}

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="mt-16 pt-10 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-primary mb-1">Let's collaborate</p>
            <p className="heading-display text-xl text-foreground">Work with our team</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary text-primary font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors group"
          >
            Get in touch
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </article>
    </Layout>
  );
};

export default LeaderDetail;
